/* eslint-disable */
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm, UseFormReturn } from 'react-hook-form';
import useYupValidationResolver from 'src/hooks/useYupValidationResolver';
import * as yup from 'yup';
import RHFInput from 'src/components/elements/Input/RHFInput';
import { Button, Checkbox, Chip, Popover } from '@mui/material';
import { twMerge } from 'tailwind-merge';

import { RangeDatePicker } from 'src/components';
import { enUS } from 'date-fns/locale';
import RHFMultiSelectChip from 'src/components/elements/MultiSelectChip/RHFMultiSelectChip';
import { BiChevronDown } from 'react-icons/bi';
import { DateRange } from 'src/components/RangeDatePicker/types';
import moment from 'moment';
import RHFMultiAutoComplete from 'src/components/elements/MultiAutoComplete/RHFMultiAutoComplete';
import RHFAutoComplete from 'src/components/elements/AutoComplete/RHFAutoComplete';
import { useClients } from 'src/hooks/query/clients';
import { useRecruiters } from 'src/hooks/query/recruiters';
import { useLocations } from 'src/hooks/query/locations';
import { jobStatusList } from '../../utils';
import RHFCheckbox from 'src/components/elements/Checkbox/RHFCheckbox';
import { useJobs } from 'src/hooks/query/jobs';

interface PopoverElementProps {
  handleClose: (element: string) => void,
  open: boolean;
  anchorEl: Element | null;
  element: string;
  children: React.ReactNode;
}

const PopoverElement = (props: PopoverElementProps) => {
  const { open, anchorEl, handleClose, element, children } = props;
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={() => handleClose(element)}
      className="pointer-events-none"
      classes={{
        paper: "pointer-events-auto"
      }}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      PaperProps={{
        onMouseLeave: () => handleClose(element)
      }}
    >
    <div className="p-2 min-w-[160px] bg-white max-w-lg">
      {children}
    </div>
    </Popover>
  )
}

const validationSchema = yup.object().shape({
  searchQuery: yup.string(),
});

export type JobListFiltersFormValues = {
  searchQuery: string;
  clients: any[];
  dateRange: DateRange | undefined;
  interviewType: any;
  jobId: any;
  jobs: any;
  title: any;
  location: any;
  recruiter: any;
  status: any;
  assignedToMe: boolean;
};

export type JobListFiltersSubmitHandler = {
  values: JobListFiltersFormValues;
  formInstance: UseFormReturn<JobListFiltersFormValues, any>;
};

interface JobListFiltersProps {
  onSubmit: (data: JobListFiltersSubmitHandler) => void;
  onReset: (isATSRecruiter: boolean) => void;
  isATSRecruiter: boolean;
}

const JobListFilters = function JobListFilters(props: JobListFiltersProps) {
  const { onSubmit, onReset, isATSRecruiter } = props;

  const {
    clientOptions,
    clientsLoading,
    fetchClients,
  } = useClients({ enabled: false });

  const {
    recruitersOptions,
    recruitersLoading,
    fetchRecruiters,
  } = useRecruiters({ enabled: false });

  const {
    jobsLoading,
    jobsOptions,
    fetchJobs,
  } = useJobs({ enabled: false });


  const {
    locationsLoading,
    locationsOptions,
    fetchLocations
  } = useLocations({enabled: false});

  const resolver = useYupValidationResolver(validationSchema);
  const methods = useForm<JobListFiltersFormValues>({
    resolver,
    defaultValues:{
      assignedToMe: isATSRecruiter || false,
      status: "ACTIVE",
    }
  });
  const {
    handleSubmit,
  } = methods;

  const submitHandler = (data: JobListFiltersFormValues) => {
    onSubmit({
      values: data,
      formInstance: methods,
    });
  };

  useEffect(() => {
      submitHandler(methods.getValues());
  }, [isATSRecruiter]);

  const [anchorEl, setAnchorEl] = useState<{ [key: string]: Element | null }>({});

  const handleOpen = (event: React.MouseEvent<HTMLDivElement>, element: string) => {
    switch(element) {
      case 'clients':
        if (clientOptions.length === 0) fetchClients();
      break;
      case 'recruiter':
        if (recruitersOptions.length === 0) fetchRecruiters();
      break;
      case 'jobs':
        if (jobsOptions.length === 0) fetchJobs();
        break;
      case 'location':
        if (locationsOptions.length === 0) fetchLocations();
      break;
    }
    setAnchorEl({ [element]: event.currentTarget });
  };

  const handleClose = (element: string) => {
    setAnchorEl((prev) => ({ ...prev, [element]: null }));
  };

  const clients = methods.watch('clients');
  const selectedClients = useMemo(() => clientOptions.filter((client) => clients?.includes(client.value)), [clients]);

  const jobs = methods.watch('jobs');
  const selectedJob = useMemo(() => jobsOptions.find((jobOption) => jobs === jobOption.value), [jobs]);

  const recruiter = methods.watch('recruiter');
  const selectedRecruiter = useMemo(() => recruitersOptions.find((recruiterOption) => recruiter === recruiterOption.value), [recruiter]);

  const location = methods.watch('location');
  const selectedLocation = useMemo(() => locationsOptions.find((locationOption) => location === locationOption.value), [location]);

  const status = methods.watch('status');
  const selectedStatus = useMemo(() => jobStatusList.find((statusOption) => status === statusOption.value), [status]);

  return (
    <>
      <form onSubmit={handleSubmit(submitHandler)} className=" mt-4 rounded-md custom-shadow bg-white">
        {/* <div className="flex w-full items-end gap-2 py-2 p-2">
          <div className="flex-1">
            <RHFInput name="searchQuery" control={methods.control} placeholder="Search" />
          </div>
          <div className="flex gap-2 items-center">
            <Button variant="contained" type="submit">Search</Button>
            <Button variant="outlined" type="reset" onClick={() => {
              methods.reset({});
              onReset(isATSRecruiter);
            }}>Reset</Button>
          </div>
        </div> */}
        <div className="grid grid-cols-9 gap-2 w-full p-2 pb-2">
          <div
            onMouseEnter={(e) => handleOpen(e, 'date')}
            onMouseLeave={(e) => handleClose('date')}
            className="flex cursor-pointer justify-between px-2 rounded-md relative py-1 items-center gap-2 text-sm">
            <span className="overflow-hidden text-ellipsis text-md">Date</span>
            <BiChevronDown className={twMerge(Boolean('date' in anchorEl && anchorEl['date']) && "rotate-180")} />
            {methods.watch('dateRange') && (
            <div className="h-[2px] absolute left-2 right-2 bottom-0 bg-primary" />
            )}

            <PopoverElement
              anchorEl={'date' in anchorEl ? anchorEl['date'] : null}
              open={Boolean('date' in anchorEl && anchorEl['date'])}
              handleClose={handleClose}
              element="date"
            >
              <Controller
                control={methods.control}
                name="dateRange"
                render={({ field }) => (
                  <RangeDatePicker
                    customCssClass="select-shadow"
                    locale={{
                      ...enUS,
                      options: {
                        weekStartsOn: 1,
                      },
                    }}
                    value={field.value}
                    open
                    onRangeChange={(e) => {
                      field.onChange(e);
                      handleClose('date');
                    }}
                  />
                )}
              />
            </PopoverElement>
          </div>
          <div
            onMouseEnter={(e) => handleOpen(e, 'clients')}
            onMouseLeave={(e) => handleClose('clients')}
            className="flex cursor-pointer justify-between px-2 rounded-md relative py-1 items-center gap-2 text-sm">
            <span className="overflow-hidden text-ellipsis text-md">Client</span>
            <BiChevronDown className={twMerge(Boolean('clients' in anchorEl && anchorEl['clients']) && "rotate-180")} />
            {methods.watch('clients') && methods.watch('clients').length > 0 && (
            <div className="h-[2px] absolute left-2 right-2 bottom-0 bg-primary" />
            )}

            <PopoverElement
              anchorEl={'clients' in anchorEl ? anchorEl['clients'] : null}
              open={Boolean('clients' in anchorEl && anchorEl['clients'])}
              handleClose={handleClose}
              element="clients"
            >
              <div className="min-w-[200px]">
                <RHFMultiAutoComplete
                  name="clients"
                  control={methods.control}
                  options={clientOptions || []}
                  loading={clientsLoading}
                  placeholder="Search clients"
                  onSelectClose={() => handleClose('clients')}
                />
              </div>
            </PopoverElement>
          </div>
          <div
            onMouseEnter={(e) => handleOpen(e, 'jobs')}
            onMouseLeave={(e) => handleClose('jobs')}
            className="flex cursor-pointer justify-between px-2 rounded-md relative py-1 items-center gap-2 text-sm">
            <span className="overflow-hidden text-ellipsis text-md">Jobs</span>
            <BiChevronDown className={twMerge(Boolean('jobs' in anchorEl && anchorEl['jobs']) && "rotate-180")} />
            {methods.watch('jobs') && (
              <div className="h-[2px] absolute left-2 right-2 bottom-0 bg-primary" />
            )}

            <PopoverElement
              anchorEl={'jobs' in anchorEl ? anchorEl['jobs'] : null}
              open={Boolean('jobs' in anchorEl && anchorEl['jobs'])}
              handleClose={handleClose}
              element="jobs"
            >
              <div className="min-w-[200px]">
                <RHFAutoComplete
                  name="jobs"
                  control={methods.control}
                  options={jobsOptions}
                  loading={jobsLoading}
                  placeholder="Search jobs"
                  onSelectClose={() => handleClose('jobs')}
                />
              </div>
            </PopoverElement>
          </div>
          <div
            onMouseEnter={(e) => handleOpen(e, 'location')}
            onMouseLeave={(e) => handleClose('location')}
            className="flex cursor-pointer justify-between px-2 rounded-md relative py-1 items-center gap-2 text-sm"
          >
            <span className="overflow-hidden text-ellipsis text-md">Location</span>
            <BiChevronDown className={twMerge(Boolean('location' in anchorEl && anchorEl['location']) && "rotate-180")} />
            {methods.watch('location') && (
            <div className="h-[2px] absolute left-2 right-2 bottom-0 bg-primary" />
            )}

            <PopoverElement
              anchorEl={'location' in anchorEl ? anchorEl['location'] : null}
              open={Boolean('location' in anchorEl && anchorEl['location'])}
              handleClose={handleClose}
              element="location"
            >
              <div className="min-w-[200px]">
                <RHFAutoComplete
                  name="location"
                  control={methods.control}
                  options={locationsOptions}
                  loading= {locationsLoading}
                  placeholder="Search location"
                />
              </div>
            </PopoverElement>
          </div>
          <div
            onMouseEnter={(e) => handleOpen(e, 'recruiter')}
            onMouseLeave={(e) => handleClose('recruiter')}
            className="flex cursor-pointer justify-between px-2 rounded-md relative py-1 items-center gap-2 text-sm"
          >
            <span className="overflow-hidden text-ellipsis text-md">Recruiter</span>
            <BiChevronDown className={twMerge(Boolean('recruiter' in anchorEl && anchorEl['recruiter']) && "rotate-180")} />
            {methods.watch('recruiter') && (
            <div className="h-[2px] absolute left-2 right-2 bottom-0 bg-primary" />
            )}

            <PopoverElement
              anchorEl={'recruiter' in anchorEl ? anchorEl['recruiter'] : null}
              open={Boolean('recruiter' in anchorEl && anchorEl['recruiter'])}
              handleClose={handleClose}
              element="recruiter"
            >
              <div className="min-w-[200px]">
                <RHFAutoComplete
                  name="recruiter"
                  control={methods.control}
                  options={recruitersOptions}
                  loading={recruitersLoading}
                  placeholder="Search recruiter"
                />
              </div>
            </PopoverElement>
          </div>
          <div
            onMouseEnter={(e) => handleOpen(e, 'status')}
            onMouseLeave={(e) => handleClose('status')}
            className="flex cursor-pointer justify-between px-2 rounded-md relative py-1 items-center gap-2 text-sm"
          >
            <span className="overflow-hidden text-ellipsis text-md">Status</span>
            <BiChevronDown className={twMerge(Boolean('status' in anchorEl && anchorEl['status']) && "rotate-180")} />
            {methods.watch('status') && (
            <div className="h-[2px] absolute left-2 right-2 bottom-0 bg-primary" />
            )}

            <PopoverElement
              anchorEl={'status' in anchorEl ? anchorEl['status'] : null}
              open={Boolean('status' in anchorEl && anchorEl['status'])}
              handleClose={handleClose}
              element="status"
            >
              <div className="min-w-[200px]">
                <RHFAutoComplete
                  name="status"
                  control={methods.control}
                  options={jobStatusList}
                  placeholder="Search status"
                />
              </div>
            </PopoverElement>
          </div>
          <div className='flex items-center min-w-[200px]'>
            <RHFCheckbox name="assignedToMe" control={methods.control} label='Assigned to me'/>
          </div>
          <div className="flex gap-2 items-center col-span-2 justify-end">
            <Button variant="contained" type="submit">Search</Button>
            <Button variant="outlined" type="reset" onClick={() => {
              methods.reset({});
              onReset(isATSRecruiter);
            }}>Reset</Button>
          </div>
        </div>
      </form>
      <div className="flex py-2 gap-2">
        {methods.watch('dateRange') && (
          <Chip
            label={`${moment(methods.watch('dateRange')?.startDate).format('MM-DD-YYYY')} - ${moment(methods.watch('dateRange')?.endDate).format('MM-DD-YYYY')}`}
            onDelete={() => {
              methods.setValue('dateRange', undefined);
            }}
          />
        )}
        {selectedClients.length > 0 && (
          <Chip
            label={`Clients: ${selectedClients.map((client) => client.label).join(', ')}`}
            onDelete={() => {
              methods.setValue('clients', []);
            }}
          />
        )}
        {selectedJob && (
          <Chip
            label={`${selectedJob.label}`}
            onDelete={() => {
              methods.setValue('jobs', undefined);
            }}
          />
        )}
        {selectedLocation && (
          <Chip
            label={`${selectedLocation?.label}`}
            onDelete={() => {
              methods.setValue('location', undefined);
            }}
          />
        )}
        {selectedRecruiter && (
          <Chip
            label={`${selectedRecruiter.label}`}
            onDelete={() => {
              methods.setValue('recruiter', undefined);
            }}
          />
        )}
        {selectedStatus && (
          <Chip
            label={`${selectedStatus.label}`}
            onDelete={() => {
              methods.setValue('status', undefined);
            }}
          />
        )}
      </div>
    </>
  );
};

JobListFilters.defaultProps = {

};

JobListFilters.propTypes = {

};

export default JobListFilters;
